import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { SelectInput } from '@/components/form/fields/select/SelectInput';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useCurrentPurchaseOrderLink } from '@/lib/getMyLinks';
import { usePoStateDropdownOptions } from '@/services/purchaseOrders/usePoStateDropdownOptions';
import { usePurchaseOrder } from '@/services/purchaseOrders/usePurchaseOrders';
import { PoStatus } from '@/services/purchaseOrders/usePurchaseOrderService';
import { useUpdatePurchaseOrderState } from '@/services/purchaseOrders/useUpdatePurchaseOrderState';
import { FC, FunctionComponent, Suspense, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

const ModalContent: FC<ModalProps<{ purchaseOrderId: string }>> = props => {
  const orderId = props.props?.purchaseOrderId;
  const { partnerId, companyId } = useParams();

  if (!(partnerId && companyId && orderId)) throw new Error('Missing params');

  const order = usePurchaseOrder(orderId);

  if (!order) throw new Error(`Order - ${orderId} not found`);

  const [loading, setLoading] = useState(false);
  const [newStatus, setNewStatus] = useState<PoStatus>(order.state);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const navigate = useNavigate();
  const poLink = useCurrentPurchaseOrderLink();

  const options = usePoStateDropdownOptions();

  const update = useUpdatePurchaseOrderState(partnerId, companyId, orderId);

  const onSubmit = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      await update(newStatus);
      props.close();
      if (newStatus === PoStatus.Draft) {
        navigate(poLink);
      }
    } catch (error) {
      setErrorMessage('Unable to update status.');
    }

    setLoading(false);
  };

  return (
    <Card className="w-full p-8 space-y-2">
      <Modal.Controls {...props}>
        <h1 className="text-2xl font-bold">Update Status</h1>
      </Modal.Controls>

      <div className="space-y-6">
        <div className="space-y-2">
          <p>
            Please select a new status for <span className="font-semibold">{order.name}.</span>
          </p>
          <SelectInput
            options={options}
            onChange={option => setNewStatus(option ? parseInt(option.id) : PoStatus.Draft)}
            id={'state'}
            value={options.find(o => o.id === newStatus.toString()) || null}
          />
          {errorMessage && <p className="text-red-500">{errorMessage}</p>}
        </div>

        <div className="flex gap-2 justify-between">
          <Button onClick={props.close}>Cancel</Button>
          <Button type="button" variant={ButtonVariant.Primary} loading={loading} onClick={onSubmit}>
            Submit
          </Button>
        </div>
      </div>
    </Card>
  );
};

const UpdateStatusModal: FunctionComponent<ModalProps<{ purchaseOrderId: string }>> = props => {
  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <ModalContent {...props} />
      </Suspense>
    </Modal>
  );
};

export default UpdateStatusModal;
